import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {formatFixedNumber, formatPercent} from '../../../../../../../shared/numberFormat'
import {
  getAmbientClassCount,
  getAmbientClassLabel,
} from '../../Ambients/ambientClassPresentation'
import './AmbientMapLegend.style.scss'

const getColor = (key, numClasses) => {
  return {
    2: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(26,150,65)',
    },
    3: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(255,255,192)',
      pp: 'rgb(26,150,65)',
    },
    4: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(254,201,129)',
      pp: 'rgb(196,230,135)',
      ap: 'rgb(26,150,65)',
    },
    5: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(253,174,97)',
      pp: 'rgb(255,255,192)',
      ap: 'rgb(166,217,106)',
      map: 'rgb(26,150,65)',
    },
    6: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(246,144,83)',
      pp: 'rgb(255,223,154)',
      ap: 'rgb(220,240,158)',
      map: 'rgb(138,204,98)',
      five: 'rgb(26,150,65)',
    },
    7: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(241,124,74)',
      pp: 'rgb(254,201,129)',
      ap: 'rgb(255,255,192)',
      map: 'rgb(196,230,135)',
      five: 'rgb(119,195,92)',
      six: 'rgb(26,150,65)',
    },
  }[parseInt(numClasses, 10)][key]
}

const getValidClasses = (numClasses) => {
  return {
    2: ['mbp', 'bp'],
    3: ['mbp', 'bp', 'pp'],
    4: ['mbp', 'bp', 'pp', 'ap'],
    5: ['mbp', 'bp', 'pp', 'ap', 'map'],
    6: ['mbp', 'bp', 'pp', 'ap', 'map', 'five'],
    7: ['mbp', 'bp', 'pp', 'ap', 'map', 'five', 'six'],
  }[numClasses]
}

const AmbientMapLegend = ({ambient, className = '', layerLabel = null, roleLabel = null}) => {
  const {t} = useTranslation()
  let formattedDate

  if (ambient?.params?.layer_date !== undefined) {
    const date = new Date(ambient.params.layer_date)
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    formattedDate = `${day}/${month}/${year}`
  }

  const data = useMemo(() => {
    const base = {
      mbp: {classIndex: 0, sort: 6, ha: 0},
      bp: {classIndex: 1, sort: 5, ha: 0},
      pp: {classIndex: 2, sort: 4, ha: 0},
      ap: {classIndex: 3, sort: 3, ha: 0},
      map: {classIndex: 4, sort: 2, ha: 0},
      five: {classIndex: 5, sort: 1, ha: 0},
      six: {classIndex: 6, sort: 0, ha: 0},
    }

    let total = 0

    ;[ambient].forEach((currentAmbient) => {
      currentAmbient.geojson.features.forEach((feature) => {
        const currentSupha = feature.properties.sup_ha
        total += feature.properties.sup_ha

        switch (feature.properties.cls) {
          case 0:
            base.mbp.ha += currentSupha
            break
          case 1:
            base.bp.ha += currentSupha
            break
          case 2:
            base.pp.ha += currentSupha
            break
          case 3:
            base.ap.ha += currentSupha
            break
          case 4:
            base.map.ha += currentSupha
            break
          case 5:
            base.five.ha += currentSupha
            break
          case 6:
            base.six.ha += currentSupha
            break
          default:
            break
        }
      })
    })

    Object.keys(base).forEach((key) => {
      base[key].percentage = total > 0 ? (base[key].ha * 100) / total : 0
    })

    const numClass = getAmbientClassCount(ambient)
    const rows = Object.keys(base)
      .map((key) => {
        base[key].cls = key
        return base[key]
      })
      .sort((left, right) => (left.sort > right.sort ? 1 : -1))
      .filter((entry) => (getValidClasses(numClass) || []).indexOf(entry.cls) !== -1)

    return {rows, max: numClass, total}
  }, [ambient])

  const rootClassName = [
    'card shadow-sm mb-5 displayable right-card-data gis-map-legend gis-map-legend--ambient',
    roleLabel ? 'gis-map-legend--comparison-card' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClassName}>
      <div className='card-header'>
        <div className='gis-map-legend__title-group'>
          <h3 className='card-title'>{roleLabel || t('ambient:legend.title')}</h3>
          {layerLabel && <span className='gis-map-legend__meta'>{layerLabel}</span>}
          {formattedDate && <span className='gis-map-legend__meta'>EVI {formattedDate}</span>}
        </div>
      </div>
      <div className='card-body'>
        <div className='gis-map-legend__table'>
          <div className='gis-map-legend__table-row gis-map-legend__table-row--head'>
            <span className='gis-map-legend__cell gis-map-legend__cell--swatch' />
            <span className='gis-map-legend__cell gis-map-legend__cell--label'>
              {t('ambient:legend.class')}
            </span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>ha</span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>%</span>
          </div>
          <div className='gis-map-legend__scale-label gis-map-legend__scale-label--top'>
            {t('ambient:legend.higher_productivity')}
          </div>
          {data.rows.map((entry, index) => (
            <div
              className='gis-map-legend__table-row'
              key={`ambient-${ambient.ambient_id}-${entry.cls}-${index}`}
            >
              <span
                className='gis-map-legend__cell gis-map-legend__cell--swatch'
                aria-hidden='true'
              >
                <span
                  className='box-square-ambient'
                  style={{backgroundColor: getColor(entry.cls, data.max)}}
                />
              </span>
              <span className='gis-map-legend__cell gis-map-legend__cell--label'>
                {getAmbientClassLabel({
                  classIndex: entry.classIndex,
                  classCount: data.max,
                  classWord: t('ambient:legend.class'),
                })}
              </span>
              <span className='gis-map-legend__cell gis-map-legend__cell--value'>
                {formatFixedNumber(entry.ha)}
              </span>
              <span className='gis-map-legend__cell gis-map-legend__cell--value'>
                {formatPercent(entry.percentage)}
              </span>
            </div>
          ))}
          <div className='gis-map-legend__scale-label gis-map-legend__scale-label--bottom'>
            {t('ambient:legend.lower_productivity')}
          </div>
          <div className='gis-map-legend__table-row gis-map-legend__table-row--total'>
            <span className='gis-map-legend__cell gis-map-legend__cell--swatch' />
            <span className='gis-map-legend__cell gis-map-legend__cell--label'>{t('ambient:legend.total')}</span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>
              {formatFixedNumber(data.total)}
            </span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>{formatPercent(100)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AmbientMapLegend
